import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';

import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { getDynVals } from '../../utils/DynHelper';
import useUsers from '../../context/UsersContext';
import useMenus from '../../context/MenuContext';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI, FormBase, ButtonUI } from '../Interface';
import CorrectionFields from './CorrectionFields';
import CorrectionReports from './CorrectionReports';
import CorrectionTabs from './CorrectionTabs';
import correctionService from '../../service/correction.service';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';

const CorrectionPage = () => {
  const { getUser, user } = useUsers();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState({});
  const [submenu, setSubmenu] = useState(null);
  const [defaultValues, setDefaultValues] = useState({});
  const [canEdit, setCanEdit] = useState(true);
  const [dynProps, setDynProps] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteData, setAutocompleteData] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    users: [],
  });
  const [loading, setLoading] = useState(true);
  const [dynLoading, setDynLoading] = useState(true);
  const isLoading = loading || dynLoading;

  const def = {
    deadline: dayjs(new Date()),
    area: '',
    origin: '',
    error: '',
    causeOfError: '',
    action: '',
    responsibleId: null,
    documentation: '',
    evaluation: '',
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getMenus();

    setDefaultValues(def);
  }, []);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          statusId: autocompleteData?.status?.defaultValue ?? null,
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          status: autocompleteData?.status?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('correction');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (submenu) initDefaultValues();
  }, [submenu]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          users: resp[0],
        };
        setAutocompleteData(aData);

        const acOptions = {
          users: generateAutocompleteItems(aData.users, 'userName', 'userId'),
        };
        setAutoCompleteOptions(acOptions);
        setLoading(false);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const valuesToForm = () => {
    const {
      prefixedId,
      deadline,
      area,
      origin,
      error,
      causeOfError,
      action,
      responsibleId,
      documentation,
      evaluation,
      dynamicProperties,
    } = selectedValues;

    setInitValues({
      deadline: dayjs(deadline),
      area,
      origin,
      error,
      prefixedId: prefixedId ?? '',
      causeOfError,
      action,
      responsibleId,
      documentation,
      evaluation,
      dynamicFields: getDynVals(dynProps, dynamicProperties),
    });
  };

  const validationSchema = Yup.object().shape({
    deadline: Yup.string().required(),
    area: Yup.string().required(),
    origin: Yup.string().required(),
    error: Yup.string().required(),
    causeOfError: Yup.string().required(),
    action: Yup.string().required(),
    responsibleId: Yup.string().required(),
    documentation: Yup.string(),
    evaluation: Yup.string(),
  });

  const insertObject = (values) => {
    const insertObject = {
      ...values,
      date: dayjs(values.date).toISOString(),
      createdBy: user.userId,
    };

    correctionService
      .createCorrection(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data[0]);

        setRowSelectionModel([data[0].id]);
      })

      .finally(() => {
        //@TODO loader
      });
  };

  const updateObject = (values) => {
    const updData = {
      ...values,
      deadline: dayjs(values.deadline).toISOString(),
      updatedBy: user.userId,
    };

    correctionService.updateCorrection(updData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      insertObject(values, resetForm);
    }
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    correctionService.deleteCorrection(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(defaultSelectedValues);
    });
  };

  const printClick = () => {
    addNotification({
      content: 'Nyomtatás elindult!',
      type: NotificationType.INFO,
    });

    correctionService.printDocument({
      docId: selectedValues.id,
    });
  };

  return (
    <>
      <FormBase
        name="Correction"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <div className="flex items-center flex-wrap">
                <FormHeaderUI
                  addClick={() => {
                    setSelectedValues(defaultSelectedValues);
                    setViewField(false);
                    setAddField(false);
                  }}
                  addDisabled={!viewField || !canEdit}
                  modClick={() => {
                    setViewField(false);
                    setEditButtonvisible(false);
                  }}
                  modDisabled={!viewField || operatorsDisabled || !canEdit}
                  deleteClick={() => {
                    setShowDeleteDialog(true);
                  }}
                  deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                  copyClick={() => {
                    setViewField(false);
                    setAddField(false);
                  }}
                  copyDisabled={!viewField || operatorsDisabled || !canEdit}
                />
                <ButtonUI
                  text="Nyomtatás"
                  size="sm"
                  className="bg-labelColor py-3"
                  onClick={() => printClick()}
                  disabled={!viewField || operatorsDisabled}
                />
              </div>
              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <CorrectionFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      autocompleteOptions={autocompleteOptions}
                    />

                    <CorrectionTabs
                      disabled={viewField}
                      selectedValues={selectedValues}
                      setSelectedValues={setSelectedValues}
                      submenuId={submenu}
                      setDefaultValues={setDefaultValues}
                      setRefetchTable={setRefetchTable}
                      dynProperties={{ dynProps, setDynProps }}
                      user={user}
                      dynLoadingState={{ dynLoading, setDynLoading }}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);
                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <>
            {!isLoading && (
              <CorrectionReports
                refetch={{ refetchTable, setRefetchTable }}
                values={{ selectedValues, setSelectedValues }}
                viewField={viewField}
                operatorsDisabled={operatorsDisabled}
                defaultValues={defaultValues}
                editButtonVisible={editButtonVisible}
                autocompleteOptions={autocompleteOptions}
                rowSelection={{ rowSelectionModel, setRowSelectionModel }}
                dynProps={dynProps}
                loading={loading || dynLoading}
              />
            )}
          </>
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default CorrectionPage;
